import React, { useEffect, useState, useRef } from 'react';
import { useSignalPredictorStore } from '../store/signalPredictorStore';
import { useMarketStore } from '../store/marketStore';
import { useSettingsStore } from '../store/settingsStore';
import { usePreferences } from '../hooks/usePreferences';
import { ntfyService } from '../services/NtfyService';
import { useDraggable } from '../hooks/useDraggable';
import { normalizeToBase } from '../utils/symbolUtils';
import './SignalPredictorOverlay.css';

// Mini candle component for visual representation (memoized)
const MiniCandle: React.FC<{
  open: number;
  high: number;
  low: number;
  close: number;
  isGreen: boolean;
}> = React.memo(({ open, high, low, close, isGreen }) => {
  const range = high - low;
  if (range === 0) return null;

  const bodyTop = Math.max(open, close);
  const bodyBottom = Math.min(open, close);
  const bodyHeight = Math.max(bodyTop - bodyBottom, range * 0.05);

  const upperWickPct = ((high - bodyTop) / range) * 100;
  const bodyPct = (bodyHeight / range) * 100;
  const lowerWickPct = ((bodyBottom - low) / range) * 100;

  return (
    <div className="mini-candle">
      <div className="mini-candle-wick upper" style={{ height: `${upperWickPct}%` }} />
      <div
        className={`mini-candle-body ${isGreen ? 'green' : 'red'}`}
        style={{ height: `${bodyPct}%` }}
      />
      <div className="mini-candle-wick lower" style={{ height: `${lowerWickPct}%` }} />
    </div>
  );
});

interface SignalPredictorOverlayProps {
  /** Optional symbol to show prediction for (for multi-chart support) */
  symbol?: string;
  /** When true, overlay is embedded in a panel (position: absolute, top-right) */
  embedded?: boolean;
}

const SignalPredictorOverlay: React.FC<SignalPredictorOverlayProps> = ({ symbol, embedded = false }) => {
  // Use symbol-specific prediction if symbol provided, otherwise fall back to legacy global prediction
  const legacyPrediction = useSignalPredictorStore((state) => state.prediction);
  const getPredictionForSymbol = useSignalPredictorStore((state) => state.getPredictionForSymbol);
  const prediction = symbol ? getPredictionForSymbol(symbol) : legacyPrediction;
  const settings = useSignalPredictorStore((state) => state.settings);
  const stats = useSignalPredictorStore((state) => state.stats);
  const forceLive = useSignalPredictorStore((state) => state.forceLive);
  const setForceLive = useSignalPredictorStore((state) => state.setForceLive);
  const isMinimized = useSignalPredictorStore((state) => state.isMinimized);
  const setMinimized = useSignalPredictorStore((state) => state.setMinimized);
  const showCandle = useSignalPredictorStore((state) => state.showCandle);
  const setShowCandle = useSignalPredictorStore((state) => state.setShowCandle);
  const showStats = useSignalPredictorStore((state) => state.showStats);
  const setShowStats = useSignalPredictorStore((state) => state.setShowStats);
  const storedPosition = useSignalPredictorStore((state) => state.position);
  const setPosition = useSignalPredictorStore((state) => state.setPosition);
  const resetStats = useSignalPredictorStore((state) => state.resetStats);
  const updateSettings = useSignalPredictorStore((state) => state.updateSettings);
  const syncFromPreferences = useSignalPredictorStore((state) => state.syncFromPreferences);

  const dataMode = useMarketStore((state) => state.dataMode);
  const tradingSettings = useSettingsStore((state) => state.wholeCandle);
  
  // Sync from server preferences when they load
  const { preferences, isLoading: prefsLoading } = usePreferences();
  const hasSyncedPrefs = useRef(false);

  useEffect(() => {
    // Sync once when preferences finish loading AND data is actually available
    // Check for signalPredictor data to ensure API response has been processed
    const hasData = preferences.signalPredictor !== undefined;
    if (!prefsLoading && hasData && !hasSyncedPrefs.current) {
      console.log('[SignalPredictor] Preferences loaded with data, syncing. isMinimized:', preferences.signalPredictor?.isMinimized);
      hasSyncedPrefs.current = true;
      syncFromPreferences();
    }
  }, [prefsLoading, preferences.signalPredictor, syncFromPreferences]);

  const [showOpacitySlider, setShowOpacitySlider] = useState(false);
  const [showNtfySettings, setShowNtfySettings] = useState(false);
  const [ntfyTesting, setNtfyTesting] = useState(false);

  // Use draggable hook for panel positioning
  const overlayRef = useRef<HTMLDivElement>(null);
  const { position, isDragging, dragHandlers, setPosition: setDraggablePosition } = useDraggable(overlayRef, {
    defaultPosition: storedPosition,
    preferenceKey: undefined, // Will use custom setPosition from store
    constrainToViewport: false,
    excludeSelectors: ['.sp-toggle-btn', '.sp-opacity-control', '.sp-ntfy-control']
  });

  // Track if we've completed initial sync from preferences
  const initialSyncDone = useRef(false);

  // Sync position FROM preferences when they load (one-time)
  useEffect(() => {
    if (!prefsLoading && !initialSyncDone.current) {
      initialSyncDone.current = true;

      // If preferences have a saved position, use it
      if (preferences.signalPredictor?.position) {
        const prefPos = preferences.signalPredictor.position;
        // Only update if different from current position
        if (prefPos.x !== position.x || prefPos.y !== position.y) {
          setDraggablePosition(prefPos);
        }
      }
    }
  }, [prefsLoading, preferences.signalPredictor?.position]);

  // Sync position TO store when dragging stops
  useEffect(() => {
    if (!isDragging && initialSyncDone.current) {
      setPosition(position);
    }
  }, [position, isDragging, setPosition]);

  // Handle ntfy test notification
  const handleTestNtfy = async () => {
    if (ntfyTesting) return;
    setNtfyTesting(true);
    try {
      const success = await ntfyService.sendTestNotification();
      if (!success) {
        console.warn('[SignalPredictor] Test notification failed');
      }
    } finally {
      setNtfyTesting(false);
    }
  };

  // Don't render in backtest mode or if disabled
  if (dataMode === 'backtest' || !settings.enabled) return null;

  const isTracking = prediction && prediction.elapsedSeconds >= settings.startTrackingAt;
  const isEarlyCandle = prediction && prediction.elapsedSeconds < settings.startTrackingAt;

  // Format time as M:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get probability color class
  const getProbClass = () => {
    if (!prediction || !prediction.direction) return '';
    return prediction.direction === 'LONG' ? 'long' : 'short';
  };

  // Calculate accuracy
  const accuracy = stats.totalAlerts > 0
    ? Math.round(((stats.signals - stats.falsePositives) / stats.totalAlerts) * 100)
    : 0;

  // Minimized - hide completely (toggle via action bar)
  if (isMinimized) {
    return null;
  }

  return (
    <div
      ref={overlayRef}
      className={`sp-overlay theme-${settings.theme} ${isDragging ? 'dragging' : ''} ${embedded ? 'embedded' : ''}`}
      style={embedded ? {
        '--sp-opacity': settings.opacity / 100,
      } as React.CSSProperties : {
        left: `${position.x}px`,
        top: `${position.y}px`,
        '--sp-opacity': settings.opacity / 100,
      } as React.CSSProperties}
      {...(embedded ? {} : dragHandlers)}
    >
      {/* Header with toggle buttons */}
      <div className="sp-header">
        <span className="sp-badge">SP</span>
        {symbol && <span className="sp-symbol">{normalizeToBase(symbol)}</span>}
        <div className="sp-toggles">
          <button
            className={`sp-toggle-btn ${showCandle ? 'active' : ''}`}
            onClick={() => setShowCandle(!showCandle)}
            title="Toggle candle view"
          >
            O
          </button>
          <button
            className={`sp-toggle-btn ${forceLive ? 'active' : ''}`}
            onClick={() => setForceLive(!forceLive)}
            title="Force live mode"
          >
            L
          </button>
          <button
            className={`sp-toggle-btn ${showStats ? 'active' : ''}`}
            onClick={() => setShowStats(!showStats)}
            title="Toggle stats panel"
          >
            #
          </button>
          <button
            className={`sp-toggle-btn ${showOpacitySlider ? 'active' : ''}`}
            onClick={() => setShowOpacitySlider(!showOpacitySlider)}
            title="Adjust opacity"
          >
            ◐
          </button>
          <button
            className={`sp-toggle-btn ${showNtfySettings ? 'active' : ''} ${settings.ntfyEnabled ? 'ntfy-on' : ''}`}
            onClick={() => setShowNtfySettings(!showNtfySettings)}
            title="Notification settings"
          >
            🔔
          </button>
          <button
            className="sp-toggle-btn minimize"
            onClick={() => setMinimized(true)}
            title="Minimize"
          >
            _
          </button>
        </div>
      </div>

      {/* Opacity slider panel */}
      {showOpacitySlider && (
        <div className="sp-opacity-control">
          <span className="sp-opacity-label">Opacity</span>
          <input
            type="range"
            min="20"
            max="100"
            value={settings.opacity}
            onChange={(e) => updateSettings({ opacity: Number(e.target.value) })}
            className="sp-opacity-slider"
          />
          <span className="sp-opacity-value">{settings.opacity}%</span>
        </div>
      )}

      {/* Ntfy settings panel */}
      {showNtfySettings && (
        <div className="sp-ntfy-control">
          <div className="sp-ntfy-row">
            <span className="sp-ntfy-label">Push Notifications</span>
            <button
              className={`sp-ntfy-toggle ${settings.ntfyEnabled ? 'active' : ''}`}
              onClick={() => updateSettings({ ntfyEnabled: !settings.ntfyEnabled })}
            >
              <span className="toggle-knob" />
            </button>
          </div>
          <div className="sp-ntfy-row">
            <input
              type="text"
              className="sp-ntfy-input"
              placeholder="ntfy.sh topic name"
              value={settings.ntfyTopic}
              onChange={(e) => updateSettings({ ntfyTopic: e.target.value })}
            />
            <button
              className="sp-ntfy-test"
              onClick={handleTestNtfy}
              disabled={!settings.ntfyTopic || ntfyTesting}
              title="Send test notification"
            >
              {ntfyTesting ? '...' : 'Test'}
            </button>
          </div>
          <div className="sp-ntfy-hint">
            Enter your ntfy.sh topic to receive alerts on your phone
          </div>
        </div>
      )}

      {/* Stats panel (collapsible) */}
      {showStats && (
        <div className="sp-stats-panel">
          <div className="sp-stat">
            <span className="sp-stat-label">Alerts</span>
            <span className="sp-stat-value">{stats.totalAlerts}</span>
          </div>
          <div className="sp-stat">
            <span className="sp-stat-label">Signals</span>
            <span className="sp-stat-value signal">{stats.signals}</span>
          </div>
          <div className="sp-stat">
            <span className="sp-stat-label">Skip</span>
            <span className="sp-stat-value skip">{stats.skipped}</span>
          </div>
          <div className="sp-stat">
            <span className="sp-stat-label">FP</span>
            <span className="sp-stat-value fp">{stats.falsePositives}</span>
          </div>
          <div className="sp-stat">
            <span className="sp-stat-label">Acc</span>
            <span className="sp-stat-value accuracy">{accuracy}%</span>
          </div>
          <button className="sp-reset-btn" onClick={resetStats} title="Reset stats">
            R
          </button>
        </div>
      )}

      {/* Main content area */}
      <div className="sp-main-content">
        {/* Mini candle visualization */}
        {showCandle && prediction?.liveCandle && (
          <div className="sp-candle-container">
            <MiniCandle
              open={prediction.liveCandle.open}
              high={prediction.liveCandle.high}
              low={prediction.liveCandle.low}
              close={prediction.liveCandle.close}
              isGreen={prediction.liveCandle.isGreen}
            />
          </div>
        )}

        {/* Data section */}
        <div className="sp-data">
          {/* Time row */}
          {prediction && (
            <div className="sp-row">
              <span className="sp-label">TIME</span>
              <span className="sp-value time">
                {new Date(prediction.candleStartTime).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </span>
              <span className="sp-value countdown">
                {formatTime(prediction.secondsToEvaluation)} left
              </span>
            </div>
          )}

          {/* Tracking status / Early state */}
          {prediction?.marketStatus === 'closed' ? (
            <div className="sp-row">
              <span className="sp-value closed">Market Closed</span>
            </div>
          ) : isTracking || forceLive ? (
            <>
              {/* Wick row */}
              <div className="sp-row">
                <span className="sp-label">WICK</span>
                <span className={`sp-value ${prediction!.wickPct <= tradingSettings.wickThresholdMax ? 'good' : 'warn'}`}>
                  {prediction!.wickPct.toFixed(1)}%
                </span>
                <span className="sp-value threshold">
                  &le;{tradingSettings.wickThresholdMax}%
                </span>
              </div>

              {/* Length/ATR row */}
              <div className="sp-row">
                <span className="sp-label">LEN</span>
                <span className="sp-value">{prediction!.candleLength.toFixed(2)}</span>
                <span className="sp-label">ATR</span>
                <span className={`sp-value ${prediction!.isLowATR ? 'warn' : ''}`}>
                  {prediction!.atr.toFixed(1)}
                </span>
              </div>

              {/* Status row */}
              <div className="sp-row status-row">
                {prediction!.direction && (
                  <span className={`sp-direction ${prediction!.direction.toLowerCase()}`}>
                    {prediction!.direction}
                  </span>
                )}
                <span className={`sp-probability ${getProbClass()}`}>
                  {Math.round(prediction!.probability)}%
                </span>
                {prediction!.willBeSkipped && (
                  <span className="sp-skip-badge">
                    {prediction!.skipReason === 'LOW_ATR' ? 'Low ATR' : prediction!.skipReason}
                  </span>
                )}
                {prediction!.alertTriggered && (
                  <span className="sp-alert-badge">Alert!</span>
                )}
              </div>
            </>
          ) : isEarlyCandle ? (
            <>
              <div className="sp-row">
                <span className="sp-value countdown-msg">
                  Tracking in {formatTime(settings.startTrackingAt - prediction!.elapsedSeconds)}
                </span>
              </div>
              {prediction?.lastCandleResult && (
                <div className="sp-row last-result">
                  <span className={`sp-last-badge ${prediction.lastCandleResult.wasSignal ? (prediction.lastCandleResult.wasSkipped ? 'skipped' : 'signal') : 'miss'}`}>
                    {prediction.lastCandleResult.wasSignal
                      ? prediction.lastCandleResult.wasSkipped
                        ? `Skipped (${prediction.lastCandleResult.skipReason})`
                        : `${prediction.lastCandleResult.direction}`
                      : 'No Signal'}
                  </span>
                  {prediction.lastCandleResult.wickPct !== undefined && !prediction.lastCandleResult.wasSignal && (
                    <span className="sp-wick-miss">
                      {prediction.lastCandleResult.wickPct.toFixed(1)}% &gt; {prediction.lastCandleResult.thresholdMax}%
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="sp-row">
              <span className="sp-value waiting">Waiting for data...</span>
            </div>
          )}
        </div>
      </div>

      {/* Market message footer */}
      {prediction?.marketMessage && (
        <div className="sp-footer">
          <span className="sp-market-msg">{prediction.marketMessage}</span>
        </div>
      )}
    </div>
  );
};

export default SignalPredictorOverlay;
